import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { ArrowRight, Leaf, Shield, Award, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { products } from '../data/products';

export const Home = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const features = [
    {
      icon: Leaf,
      title: "100% Natural",
      description: "Pure Ayurvedic herbs sourced from trusted farmers"
    },
    {
      icon: Shield,
      title: "GMP Certified",
      description: "Manufactured in state-of-the-art facilities"
    },
    {
      icon: Award,
      title: "AYUSH Approved",
      description: "Certified by Government of India standards"
    },
    {
      icon: Sparkles,
      title: "Time-Tested",
      description: "Ancient formulations backed by modern research"
    }
  ];

  return (
    <div data-testid="home-page" className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#FAF9F6] to-[#F2F0E9]"></div>
        
        {/* Decorative elements */}
        <div className="absolute top-20 left-10 w-64 h-64 bg-[#C69C6D]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#2D4A3E]/5 rounded-full blur-3xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Text Content */}
            <div className="lg:col-span-5 space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#2D4A3E]/10 rounded-full">
                <Leaf className="w-4 h-4 text-[#2D4A3E]" />
                <span className="text-sm font-medium text-[#2D4A3E]">Authentic Ayurvedic Healing</span>
              </div>
              
              <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-[#1C1C1C] leading-[0.95] animate-fade-in-up">
                Nature's <span className="text-[#2D4A3E]">Wisdom</span>,<br />
                Your <span className="text-[#C69C6D]">Wellness</span>
              </h1>
              
              <p className="text-lg md:text-xl text-[#57534E] leading-relaxed max-w-md animate-fade-in-up animation-delay-100">
                Discover the healing power of Ayurveda with Amjatha Pharma's 
                range of authentic herbal remedies, crafted with care for your holistic well-being.
              </p>

              <p className="text-sm font-medium text-[#C69C6D] italic animate-fade-in-up animation-delay-100">
                Proudly made in Kerala, India
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-200">
                <Link to="/#products">
                  <Button 
                    data-testid="hero-explore-btn"
                    className="bg-[#2D4A3E] text-white hover:bg-[#1F352B] rounded-full px-8 py-6 text-lg transition-all duration-300 shadow-lg hover:shadow-[#2D4A3E]/20 hover:-translate-y-1"
                    onClick={() => {
                      const element = document.getElementById('products');
                      if (element) element.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    Explore Products
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/about">
                  <Button 
                    data-testid="hero-story-btn"
                    variant="outline"
                    className="border-2 border-[#2D4A3E] text-[#2D4A3E] hover:bg-[#2D4A3E] hover:text-white rounded-full px-8 py-6 text-lg transition-all duration-300"
                  >
                    Our Story
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Hero Image */}
            <div className="lg:col-span-7 relative animate-fade-in-up animation-delay-300">
              <div className="relative">
                {/* Decorative frame */}
                <div className="absolute -inset-4 bg-gradient-to-br from-[#C69C6D]/20 to-[#2D4A3E]/10 rounded-3xl"></div>
                <img
                  src="https://customer-assets.emergentagent.com/job_a2fbe1d7-6ea8-4e85-b2df-dd1bbccb4e13/artifacts/scap442x_IMG_2774.jpeg"
                  alt="Amjatha Pharma Product Range"
                  data-testid="hero-image"
                  className="relative w-full h-auto rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-[#F2F0E9]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {features.map((feature, index) => (
              <div 
                key={feature.title}
                data-testid={`feature-${index}`}
                className="group p-6 md:p-8 bg-white rounded-2xl border border-[#E7E5E4] hover:border-[#C69C6D]/30 transition-all duration-300 hover:shadow-lg"
              >
                <div className="w-12 h-12 mb-4 rounded-xl bg-[#2D4A3E]/10 flex items-center justify-center group-hover:bg-[#2D4A3E] transition-colors">
                  <feature.icon className="w-6 h-6 text-[#2D4A3E] group-hover:text-white transition-colors" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-xl font-semibold text-[#1C1C1C] mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-[#57534E]">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-24 md:py-32 bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Section Header */}
          <div className="text-center mb-16">
            <span className="text-sm font-semibold tracking-widest uppercase text-[#C69C6D] mb-4 block">
              Our Products
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#1C1C1C] mb-6">
              Ayurvedic Solutions for<br />Every Need
            </h2>
            <p className="text-lg text-[#57534E] max-w-2xl mx-auto">
              Each product is carefully formulated using authentic Ayurvedic herbs 
              and time-tested recipes for maximum effectiveness.
            </p>
          </div>

          {/* Products Grid - With Images */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Featured Product - Adloda-EX (Large) */}
            <div className="md:col-span-2">
              <div 
                data-testid={`product-card-${products[0].id}`}
                className="group relative h-full min-h-[400px] md:min-h-[450px] rounded-2xl border border-[#E7E5E4] overflow-hidden hover:shadow-xl transition-all duration-500"
                style={{ backgroundColor: products[0].bgColor }}
              >
                {/* Product Image - full image without cropping */}
                <Link to={`/products/${products[0].id}`} className="absolute right-0 md:right-4 top-1/2 -translate-y-1/2 w-[50%] md:w-[45%] h-[95%] z-10 flex items-center justify-center">
                  <img 
                    src={products[0].homeImage} 
                    alt={products[0].name}
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                  />
                </Link>
                <div className="relative p-8 md:p-12 h-full flex flex-col justify-between max-w-[50%] md:max-w-[50%]">
                  <div>
                    <span className="inline-block px-3 py-1 bg-[#B91C1C]/10 text-[#B91C1C] text-sm font-medium rounded-full mb-4">
                      Bestseller
                    </span>
                    <h3 className="font-heading text-3xl md:text-4xl font-bold text-[#1C1C1C] mb-2 group-hover:text-[#2D4A3E] transition-colors">
                      {products[0].name}
                    </h3>
                    <p className="text-[#C69C6D] font-medium mb-4">{products[0].tagline}</p>
                    <p className="text-[#57534E] text-sm md:text-base">{products[0].shortDescription}</p>
                  </div>
                  <div className="flex items-center gap-4 mt-6">
                    <Link to={`/contact?subject=${encodeURIComponent(products[0].name)}`}>
                      <Button className="bg-[#2D4A3E] text-white hover:bg-[#1F352B] rounded-full px-6 py-2 text-sm">
                        Enquire
                      </Button>
                    </Link>
                    <Link to={`/products/${products[0].id}`} className="text-[#2D4A3E] font-medium text-sm hover:underline">
                      Learn More →
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Other Products */}
            {products.slice(1).map((product) => {
              return (
                <div key={product.id}>
                  <div 
                    data-testid={`product-card-${product.id}`}
                    className="group relative h-full min-h-[380px] md:min-h-[400px] rounded-2xl border border-[#E7E5E4] overflow-hidden hover:shadow-xl transition-all duration-500"
                    style={{ backgroundColor: product.bgColor }}
                  >
                    {/* Product Image - full image without cropping */}
                    <Link to={`/products/${product.id}`} className="absolute right-0 md:right-2 top-1/2 -translate-y-1/2 w-[50%] md:w-[50%] h-[95%] z-10 flex items-center justify-center">
                      <img 
                        src={product.homeImage} 
                        alt={product.name}
                        className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                      />
                    </Link>
                    <div className="relative p-6 md:p-8 h-full flex flex-col justify-between max-w-[50%] md:max-w-[48%]">
                      <div>
                        <span className="inline-block px-3 py-1 bg-[#2D4A3E]/10 text-[#2D4A3E] text-sm font-medium rounded-full mb-4">
                          {product.category}
                        </span>
                        <h3 className="font-heading text-2xl md:text-3xl font-bold text-[#1C1C1C] mb-2 group-hover:text-[#2D4A3E] transition-colors">
                          {product.name}
                        </h3>
                        <p className="text-[#C69C6D] font-medium mb-2">{product.tagline}</p>
                        <p className="text-[#57534E] text-sm">{product.shortDescription}</p>
                      </div>
                      <div className="flex items-center gap-4 mt-4">
                        <Link to={`/contact?subject=${encodeURIComponent(product.name)}`}>
                          <Button className="bg-[#2D4A3E] text-white hover:bg-[#1F352B] rounded-full px-5 py-2 text-sm">
                            Enquire
                          </Button>
                        </Link>
                        <Link to={`/products/${product.id}`} className="text-[#2D4A3E] font-medium text-sm hover:underline">
                          Learn More →
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#2D4A3E] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#C69C6D] rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
            Experience the Power of<br />Authentic Ayurveda
          </h2>
          <p className="text-lg text-white/80 max-w-2xl mx-auto mb-4">
            Join thousands of satisfied customers who have discovered natural wellness 
            through our time-tested Ayurvedic formulations.
          </p>
          <p className="text-sm text-[#C69C6D] font-medium italic mb-10">
            Proudly made in Kerala, India
          </p>
          <Link to="/contact">
            <Button 
              data-testid="cta-contact-btn"
              className="bg-[#C69C6D] text-white hover:bg-[#B0885A] rounded-full px-10 py-6 text-lg transition-all duration-300 shadow-lg hover:shadow-[#C69C6D]/30 hover:-translate-y-1"
            >
              Contact Us Today
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
